import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import FeatureToggleButton from './FeatureToggleButton';
import CategorySelector from './CategorySelector';
import CityMenu from './CityMenu';
import LanguageSelector from './LanguageSelector';
import CountrySelector from './CountrySelector';
import FriendsMenu from './FriendsMenu';
import TextPlaceholder from './TextPlaceholder';
import FEATURE_FLAGS from '../config/featureFlags';
import { useAuth } from '../contexts/AuthContext';
import { useMenu } from '../contexts/MenuContext';
import { useSearch } from '../contexts/SearchContext';
import { useTranslation } from '../hooks/useTranslation';
import useMobile from "../hooks/useMobile.ts";
import { useDisclaimer } from '../contexts/DisclaimerContext.tsx';
import UserDropdown from './UserDropdown';

const Header: React.FC = () => {
  const { isMenuOpen, setIsMenuOpen } = useMenu();
  const { isAuthenticated, logout, user } = useAuth();
  const { toggleMobileSearch } = useSearch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAcceptedDisclaimer } = useDisclaimer();
  const isMobile = useMobile()

  const toggleMenu = () => {
    console.log('Toggle menu clicked, current state:', isMenuOpen);
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAuthClick = () => {
    navigate('/auth');
    setIsMenuOpen(false);
  };

  const py = isMobile ? 'py-2' : 'py-4';

  return (
    <header className="bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-md">

      <div className="container mx-auto px-4">
        <div className={`flex justify-between items-center ${py}`}>
          <Link to="/" className="text-2xl font-bold tracking-tight" style={{ minHeight: '2rem', minWidth: '12ch' }}>
            <TextPlaceholder 
              translationKey="common.escortfun_net"
              className="text-2xl font-bold"
              minHeight="2rem"
              fallbackText="EscortFun.net"
            />
          </Link>

          {hasAcceptedDisclaimer && 
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="hover:text-pink-200 transition-colors duration-200" style={{ minWidth: '4ch' }}>
              <TextPlaceholder 
                translationKey="header.homeLink"
                minHeight="1.5rem"
                fallbackText="Home"
              />
            </Link>
            <CategorySelector />
            <CityMenu />
            <FriendsMenu />
            <Link to="/blog" className="hover:text-pink-200 transition-colors duration-200" style={{ minWidth: '4ch' }}>
              <TextPlaceholder 
                translationKey="common.blogLink"
                minHeight="1.5rem"
                fallbackText="Blog"
              />
            </Link>
            <Link to="/contatti" className="hover:text-pink-200 transition-colors duration-200" style={{ minWidth: '6ch' }}>
              <TextPlaceholder 
                translationKey="common.contatti"
                minHeight="1.5rem"
                fallbackText="Contatti"
              />
            </Link>
          </nav>}

          {hasAcceptedDisclaimer &&
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={toggleMobileSearch}
              className="p-2 hover:bg-white/10 rounded-full transition-colors duration-200"
              aria-label={t('header.toggleSearchLabel')}
            >
              <Search className="h-6 w-6" />
            </button>
            <button
              onClick={toggleMenu}
              className="p-2 hover:bg-white/10 rounded-full transition-colors duration-200 z-40"
              aria-label={t('header.toggleMenuLabel')}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>}

          {hasAcceptedDisclaimer &&
          <div className="hidden md:flex items-center space-x-4">
            <CountrySelector />
            <LanguageSelector />
            <FeatureToggleButton
              isFeatureEnabled={FEATURE_FLAGS.SHOW_POST_AD}
              link="/post-ad"
              text=""
              variant="primary"
              size="sm"
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
            >
              <div style={{ minWidth: '8ch', minHeight: '2rem', display: 'flex', alignItems: 'center' }}>
                <TextPlaceholder 
                  translationKey="header.postAdButton"
                  minHeight="1.5rem"
                  fallbackText="Inserisci"
                />
              </div>
            </FeatureToggleButton>

            {isAuthenticated ? (
              <UserDropdown
                username={user?.username || 'User'}
                role={user?.role || ''}
                onLogout={handleLogout}
                logoutLabel={t('header.logoutButton')}
              />
            ) : (
              FEATURE_FLAGS.SHOW_LOGIN_REGISTER && (
                <button
                  onClick={handleAuthClick}
                  className="px-4 py-2 border border-white rounded-md hover:bg-white hover:text-pink-500 transition-colors duration-200"
                  aria-label={t('common.accedi_registrati')}
                >
                  {t('common.accedi_registrati')}
                </button>
              )
            )}
          </div>}

        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white/10 backdrop-blur-sm rounded-lg mb-4 p-4">
            <nav className="flex flex-col space-y-3">
              <Link
                to="/"
                className="hover:text-pink-200 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('header.homeLink')}
              </Link>
              <div className="py-0 px-0">
                <CategorySelector/>
              </div>
              <div className="py-0 px-0">
                <CityMenu/>
              </div>
              <div className="py-0 px-0">
                <FriendsMenu isMobile={true} />
              </div>
              <Link
                to="/blog"
                className="hover:text-pink-200 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('common.blogLink')}
              </Link>
              <Link
                to="/contatti"
                className="hover:text-pink-200 transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('common.contatti')}
              </Link>
              
              {/* Country Selector per mobile */}
              <div className="py-2">
                <CountrySelector />
              </div>
              
              {/* Language Selector per mobile */}
              <div className="py-2">
                <LanguageSelector />
              </div>

              {isAuthenticated ? (
                <UserDropdown
                  username={user?.username || 'User'}
                  role={user?.role || ''}
                  onLogout={handleLogout}
                  logoutLabel={t('header.logoutButton')}
                  flat
                  onLinkClick={() => setIsMenuOpen(false)}
                />
              ) : (
                FEATURE_FLAGS.SHOW_LOGIN_REGISTER && (
                  <button
                    onClick={handleAuthClick}
                    className="text-center px-4 py-2 border border-white/50 rounded-md hover:bg-white/10 transition-colors duration-200 mt-2"
                    aria-label={t('common.accedi_registrati')}
                  >
                    {t('common.accedi_registrati')}
                  </button>
                )
              )}
            </nav>
          </div>
        )}
      </div>

    </header>
  );
};

export default Header;
